import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSavedLocationListApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import type { RedirectProp } from '@hero/web-hooks';
import { SavedLocationIcon } from '@hero/web-icons';
import { SavedLocationTable } from './SavedLocationTable';
import { ButtonCreateEntity } from '../../../app/ButtonCreateEntity';
import { PageBack } from '../../../app/PageBack';
import { TableEmptyState } from '../../../app/Table';
import { SavedLocationDrawer } from '../Drawer';
import { OrderDrawer } from '../../Order/Drawer';
import { LocationEditModal } from '../Modal';

export interface SavedLocationTableManagerProps {
  displayForUser?: 'customer' | 'carrier';
  /**
   * Mount the SavedLocation / Order drawers from this manager. Defaults to true so
   * standalone usage keeps working. Pages that already mount these drawers themselves
   * (e.g. CRMPage) should pass `false` to avoid duplicate drawer instances fighting
   * for the same zustand store and URL params.
   */
  renderDrawers?: boolean;
  /**
   * Render a `<PageBack>` above the table. Hidden by default; pass
   * `true` to default to the More screen, or a typed route name /
   * config for a custom destination. See `RedirectProp` for the full
   * shape.
   */
  redirect?: RedirectProp;
}

export function SavedLocationTableManager({
  displayForUser,
  renderDrawers = true,
  redirect,
}: SavedLocationTableManagerProps) {
  const { t } = useTranslation(['m-location']);
  const { isEnabled: canCreateSavedLocation } = useFeature(
    'saved_location_module',
    'sub_create_saved_location',
  );

  const {
    data: locations,
    isLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    runQuery,
  } = useSavedLocationListApi({});

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      runQuery({ search: term || undefined });
    },
    [runQuery],
  );

  const handleStatusFilterChange = useCallback(
    (value: string | undefined) => {
      const next = value === statusFilter ? undefined : value;
      setStatusFilter(next);
      runQuery({ isOwnBranch: next === 'own' ? true : next === 'client' ? false : undefined });
    },
    [runQuery, statusFilter],
  );

  const handleSort = useCallback(
    (column: string) => {
      const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setSortColumn(column);
    },
    [sortColumn, sortDirection],
  );

  const statusFilterOptions = useMemo(
    () => [
      {
        value: 'own',
        label: t('m-location:location_type.my_location.title', 'Own Branch'),
        className: 'bg-primary-100 text-logoBlue',
      },
      {
        value: 'client',
        label: t('m-location:location_type.for_client.title', 'Client Location'),
        className: 'bg-gray-100 text-gray-600',
      },
    ],
    [t],
  );

  const emptyState = (
    <TableEmptyState
      icon={<SavedLocationIcon className="h-7 w-7" />}
      title={t('m-location:table.empty.title')}
      description={t('m-location:table.empty.description')}
      action={
        canCreateSavedLocation ? (
          <ButtonCreateEntity
            label={t('m-location:table.empty.cta')}
            onClick={() => setIsCreateOpen(true)}
          />
        ) : undefined
      }
    />
  );

  return (
    <>
      {redirect ? <PageBack redirect={redirect} /> : null}
      <SavedLocationTable
        locations={locations}
        isLoading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        searchProps={{ term: searchTerm, setTerm: handleSearch }}
        onSort={handleSort}
        onLoadMore={onLoadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        emptyState={emptyState}
        hasActiveFilters={Boolean(searchTerm) || Boolean(statusFilter)}>
        <SavedLocationTable.Head>
          <SavedLocationTable.Search />
          <div className="flex items-center gap-3">
            <SavedLocationTable.Badge
              options={statusFilterOptions}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              showCounts={false}
            />
            {canCreateSavedLocation && (
              <ButtonCreateEntity
                label={t('m-location:modal.save_location_title')}
                onClick={() => setIsCreateOpen(true)}
              />
            )}
          </div>
        </SavedLocationTable.Head>
      </SavedLocationTable>

      {renderDrawers && (
        <>
          <SavedLocationDrawer displayForUser={displayForUser} syncUrl />
          <OrderDrawer displayForUser={displayForUser || 'customer'} />
        </>
      )}
      <LocationEditModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onLocationSaved={() => setIsCreateOpen(false)}
        mode="create"
      />
    </>
  );
}
